import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate } from 'react-router-dom';
import {
    Box,
    IconButton,
    TableCell,
    TableRow,
    Tooltip,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import {
    Ban,
    CheckCircle2,
    Clock,
    Lock,
    Ticket,
    Unlock,
    Wifi,
    WifiOff,
    XCircle,
} from 'lucide-react';
import { makeDepotDetailsUrl, paths } from '../../routes';
import { useCanViewAllTenants } from '../../common/useCanViewAllTenants';
import { useAccessibleTenants } from '../../common/useAccessibleTenants';
import { getTenantDisplayName } from '../../common/tenantDisplayNames';
import { MasterStation } from '../../api/master/masterApiTypes';
import { useDispatch } from 'react-redux';
import {
    masterApiSlice,
    useGetMasterDoorsQuery,
    useGetMasterStationsQuery,
} from '../../api/master/masterApiSlice';
import MasterListScaffold, { MasterColumn } from './MasterListScaffold';
import { useMasterListState } from './useMasterListState';
import { useMasterTenantData } from './useMasterTenantData';
import { onRowClick } from '../../utils/rowClick';

const ROWS_PER_PAGE_OPTIONS = [100, 200, 300, 500];
const ROWS_PER_PAGE_STORAGE_KEY = 'master_lockers_rows_per_page';

/**
 * Tenants known to operate without lockers / access management today
 * (kolula ROOM customers using guest apartments or venues, no smart
 * lockers). Excluded from the cross-tenant lockers view so the chip
 * filter and queue don't surface them. Expand as we confirm more.
 */
const TENANTS_WITHOUT_LOCKERS = new Set<string>(['neuland']);

/**
 * Inline icon constants — sized small and right next to one another so
 * three icons fit comfortably inside a `<TableCell>` without wrapping.
 */
const ICON_SIZE = 16;
const ICON_COLOR_OK = '#27AE60';
const ICON_COLOR_BAD = '#E72525';
const ICON_COLOR_UNKNOWN = '#9CA3AF';
const ICON_COLOR_WARN = '#D97706';

/**
 * Child row used both for the "expanded doors" rows and for inline
 * loading / error placeholders. We render only the locker / locker-status
 * columns so the four station-level columns stay visually empty under
 * the parent.
 */
const renderDoorPlaceholderRow = (
    keySuffix: string,
    label: string,
    bg: string,
): React.ReactElement => (
    <TableRow key={keySuffix} sx={{ backgroundColor: bg }}>
        <TableCell />
        <TableCell />
        <TableCell />
        <TableCell />
        <TableCell
            colSpan={2}
            sx={{ color: 'rgba(0,0,0,0.6)', fontStyle: 'italic' }}
        >
            {label}
        </TableCell>
    </TableRow>
);

/**
 * Doors-of-a-station section. Mounted only when the parent station is
 * expanded so we never fire the doors query for collapsed stations.
 * Keeping it as its own component preserves stable RTK Query hook order
 * for the page (the page itself never conditionally renders a hook).
 */
interface StationDoorsProps {
    tenant: string;
    stationId: string;
}

const StationDoors: React.FC<StationDoorsProps> = ({ tenant, stationId }) => {
    const { t } = useTranslation();
    const { data, isError, isFetching, isSuccess } = useGetMasterDoorsQuery({
        tenant,
        stationId,
    });

    if (isFetching) {
        return renderDoorPlaceholderRow(
            `door-loading-${tenant}-${stationId}`,
            t('master.door.loading'),
            '#FAFAFA',
        );
    }
    if (isError) {
        return renderDoorPlaceholderRow(
            `door-failed-${tenant}-${stationId}`,
            t('master.door.failed'),
            '#FEF2F2',
        );
    }
    if (!isSuccess || !data || data.length === 0) {
        return renderDoorPlaceholderRow(
            `door-empty-${tenant}-${stationId}`,
            t('master.door.loading'),
            '#FAFAFA',
        );
    }

    return (
        <>
            {data.map((door, idx) => {
                const assetStateLower = door.assetState.toLowerCase();
                const isAvailable = assetStateLower === 'available';
                const isInUse = assetStateLower === 'inuse';
                const isSoonAvailable = assetStateLower === 'soonavailable';
                const isBlocked = assetStateLower === 'blocked';

                const assetTooltip = isAvailable
                    ? t('master.door.asset_state.available')
                    : isInUse
                        ? t('master.door.asset_state.in_use')
                        : isSoonAvailable
                            ? t('master.door.asset_state.soon_available')
                            : isBlocked
                                ? t('master.door.asset_state.blocked')
                                : door.assetState;

                const doorIsOpen = door.doorState.toLowerCase() === 'open';
                const lockTooltip = doorIsOpen
                    ? t('master.door.state.open')
                    : t('master.door.state.closed');

                return (
                    <TableRow
                        key={`door-${tenant}-${stationId}-${door.doorNumber}`}
                        sx={{
                            backgroundColor: idx % 2 === 0 ? '#FAFAFA' : '#F3F4F6',
                            borderLeft: '3px solid #D1D5DB',
                        }}
                    >
                        <TableCell />
                        <TableCell />
                        <TableCell />
                        <TableCell />
                        <TableCell>
                            <Box sx={{ pl: 4 }}>
                                {t('master.door.label', { number: door.doorNumber })}
                            </Box>
                        </TableCell>
                        <TableCell>
                            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
                                <Tooltip title={assetTooltip}>
                                    <span style={{ display: 'inline-flex' }}>
                                        {isAvailable && (
                                            <CheckCircle2
                                                size={ICON_SIZE}
                                                color={ICON_COLOR_OK}
                                            />
                                        )}
                                        {(isInUse || isSoonAvailable) && (
                                            <Clock
                                                size={ICON_SIZE}
                                                color={ICON_COLOR_WARN}
                                            />
                                        )}
                                        {isBlocked && (
                                            <Ban
                                                size={ICON_SIZE}
                                                color={ICON_COLOR_BAD}
                                            />
                                        )}
                                        {!isAvailable
                                            && !isInUse
                                            && !isSoonAvailable
                                            && !isBlocked && (
                                            <Clock
                                                size={ICON_SIZE}
                                                color={ICON_COLOR_UNKNOWN}
                                            />
                                        )}
                                    </span>
                                </Tooltip>
                                <Tooltip title={lockTooltip}>
                                    <span style={{ display: 'inline-flex' }}>
                                        {doorIsOpen ? (
                                            <Unlock
                                                size={ICON_SIZE}
                                                color={ICON_COLOR_BAD}
                                            />
                                        ) : (
                                            <Lock
                                                size={ICON_SIZE}
                                                color={ICON_COLOR_OK}
                                            />
                                        )}
                                    </span>
                                </Tooltip>
                            </Box>
                        </TableCell>
                    </TableRow>
                );
            })}
        </>
    );
};

const MasterLockersPage: React.FC = () => {
    const { t } = useTranslation();
    const canViewAllTenants = useCanViewAllTenants();
    const allAccessibleTenants = useAccessibleTenants();
    const accessibleTenants = useMemo(
        () => allAccessibleTenants.filter((slug) => !TENANTS_WITHOUT_LOCKERS.has(slug)),
        [allAccessibleTenants],
    );

    const list = useMasterListState({
        accessibleTenants,
        rowsPerPageStorageKey: ROWS_PER_PAGE_STORAGE_KEY,
        // Default sort: station name ascending.
        defaultSort: { key: 'station', order: 'asc' },
        rowsPerPageOptions: ROWS_PER_PAGE_OPTIONS,
    });

    const { rowsByTenant, failedTenants, loadedTenants, querySlots } =
        useMasterTenantData(accessibleTenants, useGetMasterStationsQuery);

    // Reload = invalidate every tenant's cached station list plus the
    // lazily loaded doors of expanded rows; mounted queries refetch and
    // the rows update in place.
    const dispatch = useDispatch();
    const handleReload = React.useCallback(() => {
        dispatch(masterApiSlice.util.invalidateTags(['masterStations', 'masterDoors']));
    }, [dispatch]);

    /**
     * Set of `${tenant}:${stationId}` keys that are currently expanded.
     * Mirrors the `expandedSeries` pattern from `BookingTable`. We key by
     * tenant+id because station IDs are only unique within a tenant.
     */
    const [expandedStations, setExpandedStations] = React.useState<Set<string>>(new Set());

    const mergedRows = useMemo(() => {
        const all: MasterStation[] = [];
        accessibleTenants.forEach((tenant) => {
            if (!list.selectedTenantSlugs.has(tenant)) return;
            const rows = rowsByTenant[tenant];
            if (rows) all.push(...rows);
        });
        return all;
    }, [rowsByTenant, accessibleTenants, list.selectedTenantSlugs]);

    const filteredRows = useMemo(() => {
        const term = list.searchQuery.trim().toLowerCase();
        if (!term) return mergedRows;
        return mergedRows.filter((row) => {
            const tenantName = getTenantDisplayName(row.tenantSlug).toLowerCase();
            const name = row.name.toLowerCase();
            const locationName = row.locationName.toLowerCase();
            return (
                tenantName.includes(term) ||
                name.includes(term) ||
                locationName.includes(term)
            );
        });
    }, [mergedRows, list.searchQuery]);

    const sortedRows = useMemo(() => {
        const compare = (a: MasterStation, b: MasterStation): number => {
            switch (list.sortState.key) {
                case 'tenant':
                    return getTenantDisplayName(a.tenantSlug)
                        .localeCompare(getTenantDisplayName(b.tenantSlug));
                case 'station':
                    return a.name.localeCompare(b.name);
                case 'location':
                    return a.locationName.localeCompare(b.locationName);
                default:
                    return 0;
            }
        };
        return list.applySort(filteredRows, compare);
    }, [filteredRows, list.applySort, list.sortState.key]);

    const pagedRows = useMemo(
        () => list.paginate(sortedRows),
        [sortedRows, list.paginate],
    );

    const handleRowClick = React.useCallback((row: MasterStation) => {
        list.drillIntoTenant(row.tenantSlug, makeDepotDetailsUrl(row.id));
    }, [list.drillIntoTenant]);

    const toggleStation = (row: MasterStation) => {
        const expansionKey = `${row.tenantSlug}:${row.id}`;
        setExpandedStations((prev) => {
            const next = new Set(prev);
            if (next.has(expansionKey)) {
                next.delete(expansionKey);
            } else {
                next.add(expansionKey);
            }
            return next;
        });
    };

    if (!canViewAllTenants) {
        return <Navigate to={paths.unauthorized} replace />;
    }

    const total = accessibleTenants.length;
    const loaded = loadedTenants.size;
    const failedCount = failedTenants.size;
    const notApplicable = t('common.not_applicable');
    const allLoaded = total > 0 && loaded === total;
    const showEmptyState =
        allLoaded && failedCount === 0 && sortedRows.length === 0;

    /**
     * Three inline icons for the station-status cell. Active uses the
     * familiar check/cross pair, bookable uses the lucide `Ticket` icon
     * (green/red/outlined gray), online uses `Wifi` / `WifiOff` plus a
     * neutral gray for unknown.
     */
    const renderStationStatusIcons = (row: MasterStation) => {
        const activeTooltip = row.isActive
            ? t('master.station_status.active')
            : t('master.station_status.inactive');

        let bookableTooltip: string;
        if (row.isBookable === true) {
            bookableTooltip = t('master.station_status.bookable');
        } else if (row.isBookable === false) {
            bookableTooltip = t('master.station_status.not_bookable');
        } else {
            bookableTooltip = t('master.station_status.unknown');
        }

        const onlineTooltip = {
            online: t('master.station_status.online'),
            offline: t('master.station_status.offline'),
            unknown: t('master.station_status.unknown'),
        }[row.onlineStatus];

        const bookableColor = row.isBookable === true
            ? ICON_COLOR_OK
            : row.isBookable === false
                ? ICON_COLOR_BAD
                : ICON_COLOR_UNKNOWN;

        return (
            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
                <Tooltip title={activeTooltip}>
                    <span style={{ display: 'inline-flex' }}>
                        {row.isActive ? (
                            <CheckCircle2 size={ICON_SIZE} color={ICON_COLOR_OK} />
                        ) : (
                            <XCircle size={ICON_SIZE} color={ICON_COLOR_UNKNOWN} />
                        )}
                    </span>
                </Tooltip>
                <Tooltip title={bookableTooltip}>
                    <span style={{ display: 'inline-flex' }}>
                        <Ticket size={ICON_SIZE} color={bookableColor} />
                    </span>
                </Tooltip>
                <Tooltip title={onlineTooltip}>
                    <span style={{ display: 'inline-flex' }}>
                        {row.onlineStatus === 'online' && (
                            <Wifi size={ICON_SIZE} color={ICON_COLOR_OK} />
                        )}
                        {row.onlineStatus === 'offline' && (
                            <WifiOff size={ICON_SIZE} color={ICON_COLOR_BAD} />
                        )}
                        {row.onlineStatus === 'unknown' && (
                            <Wifi size={ICON_SIZE} color={ICON_COLOR_UNKNOWN} />
                        )}
                    </span>
                </Tooltip>
            </Box>
        );
    };

    const columns: MasterColumn[] = [
        { label: t('master.columns.tenant'), sortKey: 'tenant' },
        { label: t('master.columns.station'), sortKey: 'station' },
        { label: t('master.columns.location'), sortKey: 'location' },
        { label: t('master.columns.station_status') },
        { label: t('master.columns.locker') },
        { label: t('master.columns.locker_status') },
    ];

    return (
        <MasterListScaffold
            controls={list}
            title={t('master.lockers')}
            subtitle={t('master.lockers_count', { count: sortedRows.length })}
            searchPlaceholder={t('master.lockers_search_placeholder')}
            columns={columns}
            totalCount={sortedRows.length}
            loadedTenants={loaded}
            totalTenants={total}
            failedTenants={Array.from(failedTenants)}
            showEmptyState={showEmptyState}
            emptyMessage={t('master.lockers_empty')}
            querySlots={querySlots}
            onReload={handleReload}
        >
            {pagedRows.map((row) => {
                const expansionKey = `${row.tenantSlug}:${row.id}`;
                const isExpanded = expandedStations.has(expansionKey);
                return (
                    <React.Fragment key={expansionKey}>
                        <TableRow
                            hover={true}
                            sx={{ cursor: 'pointer' }}
                            onClick={onRowClick(() => handleRowClick(row))}
                        >
                            <TableCell>
                                {getTenantDisplayName(row.tenantSlug)}
                            </TableCell>
                            <TableCell>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <IconButton
                                        size={'small'}
                                        sx={{ p: 0 }}
                                        onClick={(event) => {
                                            event.stopPropagation();
                                            toggleStation(row);
                                        }}
                                    >
                                        {isExpanded
                                            ? <KeyboardArrowDownIcon fontSize={'small'} />
                                            : <KeyboardArrowRightIcon fontSize={'small'} />}
                                    </IconButton>
                                    <span>{row.name || notApplicable}</span>
                                </Box>
                            </TableCell>
                            <TableCell>
                                {row.locationName || notApplicable}
                            </TableCell>
                            <TableCell>
                                {renderStationStatusIcons(row)}
                            </TableCell>
                            <TableCell />
                            <TableCell />
                        </TableRow>
                        {isExpanded && (
                            <StationDoors
                                tenant={row.tenantSlug}
                                stationId={row.id}
                            />
                        )}
                    </React.Fragment>
                );
            })}
        </MasterListScaffold>
    );
};

export default MasterLockersPage;
